import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { THEME_GROUPS, type ThemeGroup } from '../lib/themeMap'
import { MobileSheet } from './MobileSheet'
import { Icons } from './TabToolbar'

const DEFAULT_MATERIAL_SEED = '#6366f1'

interface ThemeSheetProps {
    open: boolean
    onClose: () => void
}

/**
 * Dedicated theme flow — mode toggle + Material seed + curated theme list.
 * Separate from CanvasSettings (which handles layers / direction / group-by).
 * Entered from the Theme icon in the top toolbar.
 */
export function ThemeSheet({ open, onClose }: ThemeSheetProps) {
    const { theme, setTheme, setThemeFromSeed, setRandomTheme, themeName } = useTheme()
    const isDark = theme.isDark ?? themeName === 'dark'

    // Match by variant key first, then fall back to theme.name for bootstrap state
    // (keeps the picker in sync during the window before KMP presets finish loading).
    const activeGroup: ThemeGroup | undefined =
        THEME_GROUPS.find(g => g.light?.key === themeName || g.dark?.key === themeName)
        ?? THEME_GROUPS.find(g => g.name === theme.name)
    const isCuratedTheme = activeGroup != null
    const isMaterial = !isCuratedTheme && theme.seedColor != null

    /** Switch to a curated group's variant matching current isDark. */
    const pickGroup = (g: ThemeGroup) => {
        const variant = isDark ? g.dark : g.light
        const key = variant?.key ?? g.light?.key ?? g.dark?.key
        if (key) setTheme(key)
    }

    /** Activate Material — seed-derived themer with the current (or default) seed. */
    const pickMaterial = () => {
        setThemeFromSeed(theme.seedColor ?? DEFAULT_MATERIAL_SEED, isDark)
    }

    /** Back to default — Carbon (the shipped default theme). */
    const handleReset = () => {
        setTheme(isDark ? 'carbon-dark' : 'carbon-light')
    }

    const content = (
        <div className="flex flex-col">
            {/* Material row — radio + label on the left; swatch + hex input + shuffle
                inline on the right when selected. */}
            <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-all">
                <button onClick={pickMaterial}
                    className="flex items-center gap-3 flex-1 text-left active:scale-[0.97] transition-all"
                    style={{ color: isMaterial ? 'var(--accent)' : 'var(--text-muted)' }}>
                    <Radio selected={isMaterial} />
                    <span className="text-xs font-bold font-mono tracking-tight">material</span>
                </button>
                {isMaterial && (
                    <div className="flex items-center gap-2 shrink-0">
                        <Swatch color={theme.accent} />
                        <ThemeHexInput theme={theme} setThemeFromSeed={setThemeFromSeed} isDark={isDark} />
                        <button onClick={setRandomTheme} className="w-5 h-5 flex items-center justify-center active:scale-[0.97] transition-colors"
                            style={{ color: 'var(--text-muted)' }} title="Shuffle">
                            {Icons.shuffle}
                        </button>
                    </div>
                )}
            </div>

            {/* Curated theme groups */}
            {THEME_GROUPS.map(g => (
                <RadioRow key={g.name}
                    label={g.name.toLowerCase()}
                    selected={activeGroup?.name === g.name}
                    onClick={() => pickGroup(g)} />
            ))}
        </div>
    )

    return <MobileSheet open={open} onClose={onClose} onReset={handleReset} icon={Icons.theme} title="Theme">{content}</MobileSheet>
}

/* ── Local sub-components (kept private; parallels CanvasSettings primitives) ── */

function Radio({ selected }: { selected: boolean }) {
    return (
        <span style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--text-muted)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {selected && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }} />}
        </span>
    )
}

function RadioRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
    return (
        <button onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all hover:bg-white/5"
            style={{ color: selected ? 'var(--accent)' : 'var(--text-muted)' }}>
            <Radio selected={selected} />
            <span className="text-xs font-bold font-mono tracking-tight">{label}</span>
        </button>
    )
}

function Swatch({ color }: { color: string }) {
    return <div className="w-4 h-4 rounded-full shrink-0" style={{ background: color }} />
}

function ThemeHexInput({ theme, setThemeFromSeed, isDark }: { theme: any; setThemeFromSeed: (c: string, d?: boolean) => void; isDark: boolean }) {
    const [hex, setHex] = useState(theme.seedColor ?? theme.accent)
    useEffect(() => { setHex(theme.seedColor ?? theme.accent) }, [theme.seedColor, theme.accent])
    const apply = () => {
        let val = hex.trim()
        if (!val.startsWith('#')) val = '#' + val
        if (/^#[0-9a-fA-F]{6}$/.test(val)) { setHex(val); setThemeFromSeed(val, isDark) }
    }
    return (
        <input type="text" value={hex} size={7} maxLength={7}
            onChange={e => setHex(e.target.value)} onBlur={apply}
            onKeyDown={e => { if (e.key === 'Enter') { apply(); (e.target as HTMLInputElement).blur() } }}
            className="text-xs font-mono font-bold bg-transparent outline-none border-b transition-colors text-right"
            style={{ width: '8ch', color: 'var(--text-muted)', borderBottomColor: 'color-mix(in srgb, var(--text-muted) 40%, transparent)' }} />
    )
}
